import { useSearchParams } from '@solidjs/router'
import * as mg from 'maplibre-gl'

import { JSX, createEffect, on, onCleanup, onMount } from 'solid-js'
import { createStore, produce } from 'solid-js/store'

import { point_to_tile, tile_to_point } from 'shared/utils'

import {
    WHEAT_MARK_KIND,
    WheatMarkGroup,
    WheatMarkKind,
    api_wheat_mark_patch,
} from 'api'

import {
    BlendIcon,
    CircleOffIcon,
    MilkIcon,
    MountainIcon,
    PinIcon,
    RotateCcwIcon,
    SatelliteIcon,
    SquareDashedIcon,
    SquareIcon,
    SquaresUniteIcon,
    WheatIcon,
} from 'icons'

import { BoxSelect } from './box-select'
import { MAP_STYLE } from './map-style'

import '../style/home.scss'

let map: mg.Map | undefined
let box_select: BoxSelect | undefined

// type SelectCoords = { x: number; y: number }

type MarkKind = 'void' | 'road' | 'village' | 'town' | 'city'
type State = {
    selected: Map<string, WheatMarkKind>
    selected_count: number
    selected_vec: WheatMarkGroup[]
    show_google: 'vec' | 'sat' | 'none'
    remark: boolean
    show_wheat: boolean
    show_updated: boolean
    show_hillshade: boolean
    allow_selecting: boolean
    show_tile_bounds: boolean
    mark: WheatMarkKind
    tz: number
    target: {
        tx: number
        ty: number
        lat: number
        lng: number
    }
}
const [state, setState] = createStore<State>({
    selected: new Map<string, MarkKind>(),
    selected_count: 0,
    show_google: 'none',
    remark: false,
    allow_selecting: true,
    show_tile_bounds: true,
    mark: 'village',
    show_updated: true,
    show_wheat: true,
    show_hillshade: false,
    tz: 12,
    target: { tx: 0, ty: 0, lat: 0, lng: 0 },
    get selected_vec() {
        let out: State['selected_vec'] = []
        let grouped: { [k in WheatMarkKind]: [number, number][] } = {
            city: [],
            road: [],
            town: [],
            village: [],
            void: [],
        }
        for (let [k, kind] of this.selected.entries()) {
            let [x, y] = k.split(',')
            grouped[kind].push([parseInt(x!), parseInt(y!)])
        }
        for (let [kind, v] of Object.entries(grouped)) {
            if (v.length == 0) continue
            out.push({ tiles: v, kind: kind as WheatMarkKind })
        }
        return out
    },
})

function update_map() {
    if (!map) return

    map.showTileBoundaries = state.show_tile_bounds

    if (state.show_google == 'none') {
        map.setLayoutProperty('fill gsat', 'visibility', 'none')
        map.setLayoutProperty('fill gvec', 'visibility', 'none')
    } else if (state.show_google == 'vec') {
        map.setLayoutProperty('fill gsat', 'visibility', 'none')
        map.setLayoutProperty('fill gvec', 'visibility', 'visible')
    } else if (state.show_google == 'sat') {
        map.setLayoutProperty('fill gsat', 'visibility', 'visible')
        map.setLayoutProperty('fill gvec', 'visibility', 'none')
    }

    map.setLayoutProperty(
        'wheat-display',
        'visibility',
        state.show_wheat ? 'visible' : 'none'
    )

    map.setLayoutProperty(
        'wheat-display-updated',
        'visibility',
        state.show_updated ? 'visible' : 'none'
    )

    map.setLayoutProperty(
        'hillshading',
        'visibility',
        state.show_hillshade ? 'visible' : 'none'
    )

    if (state.allow_selecting) {
        map.dragRotate.disable()
    } else {
        map.dragRotate.enable()
    }
}

export default () => {
    const [search, setSearch] = useSearchParams()

    function on_move(e: mg.MapMovementEvent) {
        if (e.type != 'moveend') return
        let center = e.target.getCenter()
        setSearch({ center: `${center.lat},${center.lng}` })
    }

    createEffect(
        on(
            () => {
                state.show_google
                state.show_wheat
                state.show_tile_bounds
                state.allow_selecting
                state.show_hillshade
                state.show_updated
            },
            () => {
                if (box_select) box_select.enabled(state.allow_selecting)

                update_map()
            }
        )
    )

    onMount(() => {
        let center: mg.LngLatLike = [51.37990859665848, 35.69307712966187]

        if (typeof search.center == 'string') {
            let [sl, sn] = search.center.split(',')
            let lat = parseFloat(sl || '')
            let lng = parseFloat(sn || '')
            if (!isNaN(lat) && !isNaN(lng)) {
                center = [lng, lat]
            }
        }

        map = new mg.Map({
            container: 'home-map',
            style: MAP_STYLE,
            // style: MAP_MAIN_STYLE,
            center,
            // center: [51.33808559203892, 35.69973203736],
            // center: [50.83833506540654, 36.83799877475306],
            pitch: 0,
            // zoom: 14,
            zoom: state.tz,
            // minZoom: 5,
            minZoom: 0,
            maxZoom: 21,
            localIdeographFontFamily: '"Vazir", sans-serif',
            // maxBounds: [26, 13, 80, 47],
            attributionControl: false,
            maplibreLogo: false,
        })

        // map.setTerrain({ source: 'hillshade', exaggeration: 1.5 })
        map.addControl(
            new mg.TerrainControl({ source: 'hillshade', exaggeration: 1.5 })
        )
        map.addControl(new mg.GlobeControl())
        map.addControl(new mg.ScaleControl({ unit: 'metric' }), 'top-right')
        map.addControl(
            new mg.NavigationControl({
                showZoom: true,
                showCompass: true,
                visualizePitch: true,
            }),
            'top-right'
        )

        map.keyboard.disable()
        map.touchZoomRotate.disableRotation()
        map.boxZoom.disable()

        box_select = new BoxSelect(map, (act, p0, p1) => {
            const min_x = Math.min(p0.x, p1.x),
                max_x = Math.max(p0.x, p1.x),
                min_y = Math.min(p0.y, p1.y),
                max_y = Math.max(p0.y, p1.y)

            // let a = tile_to_point({ ...p0, z: 18 })
            // let b = tile_to_point({ ...p1, z: 18 })
            //
            // let q = map!.querySourceFeatures('wheat', {sourceLayer: 'wheat-display'})
            // let q = map!.querySourceFeatures('wheat'[
            //     { x: a.lat, y: a.lng },
            //     { x: b.lat, y: b.lng },
            // ] as [mg.PointLike, mg.PointLike])
            // console.log(q)

            setState(
                produce(s => {
                    for (let x = min_x; x <= max_x; x++) {
                        for (let y = min_y; y <= max_y; y++) {
                            let k = `${x},${y}`
                            if (act == 'del') {
                                s.selected.delete(k)
                            } else {
                                s.selected.set(k, state.mark)
                            }
                        }
                    }
                    s.selected_count = s.selected.size
                })
            )
            selected_update()
        })

        box_select.enabled(state.allow_selecting)

        map.on('styledata', on_styledata)
        map.on('load', on_load)
        map.on('click', on_click)
        map.on('moveend', on_move)

        onCleanup(() => {
            if (box_select) box_select.clean()

            if (map) {
                map.off('styledata', on_styledata)
                map.off('load', on_load)
                map.off('click', on_click)
                map.off('moveend', on_move)
                map.remove()
                map = undefined
            }
        })
    })

    const SHOW_GOOGLE: { [k in State['show_google']]: () => JSX.Element } = {
        none: CircleOffIcon,
        sat: SatelliteIcon,
        vec: SquaresUniteIcon,
    }

    return (
        <div class='home-fnd'>
            <div class='actions'>
                <button class='styled btn-save' onclick={save}>
                    Save
                </button>
                <button
                    class='styled icon'
                    title={`show google: ${state.show_google}`}
                    onclick={() =>
                        setState(
                            produce(s => {
                                if (s.show_google == 'none') {
                                    s.show_google = 'vec'
                                } else if (s.show_google == 'vec') {
                                    s.show_google = 'sat'
                                } else {
                                    s.show_google = 'none'
                                }
                            })
                        )
                    }
                >
                    {SHOW_GOOGLE[state.show_google]()}
                </button>
                <button
                    class='styled icon'
                    title='wheat refresh'
                    onclick={wheat_refresh}
                >
                    <RotateCcwIcon />
                </button>
                <button
                    class='styled icon btn-cb'
                    title='allow selecting'
                    classList={{ active: state.allow_selecting }}
                    onclick={() =>
                        setState(s => ({ allow_selecting: !s.allow_selecting }))
                    }
                >
                    <SquareDashedIcon />
                </button>
                <select
                    class='selection-mark'
                    value={state.mark}
                    onChange={e => {
                        setState({
                            mark: e.currentTarget.value as WheatMarkKind,
                        })
                    }}
                >
                    {WHEAT_MARK_KIND.map(k => (
                        <option>{k}</option>
                    ))}
                </select>
                <button
                    class='styled icon btn-cb'
                    title='remark'
                    classList={{ active: state.remark }}
                    onclick={() => setState(s => ({ remark: !s.remark }))}
                >
                    <BlendIcon />
                </button>
                <button
                    class='styled icon btn-cb'
                    title='show wheat'
                    classList={{ active: state.show_wheat }}
                    onclick={() =>
                        setState(s => ({ show_wheat: !s.show_wheat }))
                    }
                >
                    <WheatIcon />
                </button>
                <button
                    class='styled icon btn-cb'
                    title='show updated'
                    classList={{ active: state.show_updated }}
                    onclick={() =>
                        setState(s => ({ show_updated: !s.show_updated }))
                    }
                >
                    <MilkIcon />
                </button>
                <button
                    class='styled icon btn-cb'
                    title='show tile bounds'
                    classList={{ active: state.show_tile_bounds }}
                    onclick={() =>
                        setState(s => ({
                            show_tile_bounds: !s.show_tile_bounds,
                        }))
                    }
                >
                    <SquareIcon />
                </button>
                <button
                    class='styled icon btn-cb'
                    title='show hillshade'
                    classList={{ active: state.show_hillshade }}
                    onclick={() =>
                        setState(s => ({
                            show_hillshade: !s.show_hillshade,
                        }))
                    }
                >
                    <MountainIcon />
                </button>
                <input
                    type='number'
                    max='22'
                    min='0'
                    id='target_tz'
                    class='styled zom'
                    value={state.tz + ''}
                    onInput={e => {
                        let tz = e.currentTarget.valueAsNumber
                        setState({ tz })
                    }}
                />
                <input
                    class='styled'
                    id='target_tx'
                    value={state.target.tx + ''}
                />
                <input
                    class='styled'
                    id='target_ty'
                    value={state.target.ty + ''}
                />
                <button
                    class='styled icon'
                    title='fly to tx,ty'
                    onClick={() => {
                        function target(id: string): number {
                            return parseInt(
                                document.querySelector<HTMLInputElement>(
                                    'input#' + id
                                )!.value
                            )
                        }

                        let tx = target('target_tx')
                        let ty = target('target_ty')
                        let tz = target('target_tz')

                        if (!map || tx == 0 || ty == 0 || tz == 0) return

                        let p = tile_to_point({ x: tx, y: ty, z: tz })
                        console.log(tx, ty, tz, p)

                        map.flyTo({
                            center: { lat: p.lat, lng: p.lng },
                            zoom: p.zom,
                        })
                    }}
                >
                    <PinIcon />
                </button>
                <a
                    href={`https://www.google.com/maps/@${state.target.lat},${state.target.lng},${state.tz}z`}
                    target='_blank'
                >
                    google maps
                </a>
                <input class='styled' placeholder='lat,lng goto' id='ll_goto' />
                <button
                    class='styled icon'
                    title='fly to lat,lng in input'
                    onClick={() => {
                        let ll =
                            document.querySelector<HTMLInputElement>(
                                'input#ll_goto'
                            )
                        if (!ll || !map) return
                        let [lt, ln] = ll.value.split(',') as [string, string]
                        let lat = parseFloat(lt)
                        let lng = parseFloat(ln)
                        map.flyTo({ center: { lat, lng }, zoom: 18 })
                    }}
                >
                    <PinIcon />
                </button>
                <span>selected: {state.selected_count}</span>
            </div>
            <div id='home-map'></div>
        </div>
    )
}

async function save() {
    if (!map) return
    // let body: WheatMarkingBody = {
    //     tiles: state.selected_xy.map(([{ x, y }, v]) => ({ x, y, mark: v })),
    // }

    await api_wheat_mark_patch({
        groups: state.selected_vec,
        remark: state.remark,
    })
    setState({ selected: new Map() })
    selected_update()

    wheat_refresh()
}

function on_click(e: mg.MapMouseEvent) {
    let lng = e.lngLat.lng,
        lat = e.lngLat.lat
    let { x, y } = point_to_tile({ lat, lng, zom: state.tz })
    setState({ target: { lat, lng, tx: x, ty: y } })
    navigator.clipboard.writeText(`${lat}, ${lng}`)
}

function on_styledata() {
    update_map()
}

function on_load() {
    selected_update()
    update_map()
}

function selected_update() {
    if (!map) return

    let gj: GeoJSON.GeoJSON = { type: 'FeatureCollection', features: [] }

    for (let group of state.selected_vec) {
        for (let [x, y] of group.tiles) {
            let p1 = tile_to_point({ x, y, z: 18 })
            let p2 = tile_to_point({ x: x + 1, y: y + 1, z: 18 })
            gj.features.push({
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [
                        [
                            [p1.lng, p1.lat],
                            [p1.lng, p2.lat],
                            [p2.lng, p2.lat],
                            [p2.lng, p1.lat],
                            [p1.lng, p1.lat],
                        ],
                    ],
                },
                properties: { mark: group.kind },
            })
        }
    }

    map.getSource<mg.GeoJSONSource>('selected')!.setData(gj)
}

function wheat_refresh() {
    if (!map) return
    map.getSource<mg.VectorTileSource>('wheat')!.setTiles([
        'apex:///api/wheat/display/{z}-{x}-{y}',
    ])

    map.getSource<mg.VectorTileSource>('farm')!.setUrl('/tilejson/')
}
